import { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { defineMessage, IntlFormatters, MessageDescriptor } from 'react-intl';

/** Shared table column header for generic status columns. */
export const statusColumnHeaderMessage = defineMessage({
  id: 'common.status',
  defaultMessage: 'Estado',
});

/** Status column header used on schedule list tables. */
export const scheduleListStatusColumnHeaderMessage = defineMessage({
  id: 'audit.schedulesListScreen.props.status',
  defaultMessage: 'Estado',
});

/**
 * Only set `align` — do not pass a column-level `sx`.
 * MRT shallow-merges column `muiTableHeadCellProps` over the table defaults, so a
 * column `sx` would replace MrtTable's header typography (`fontWeight: 500`) and
 * leave MRT's default `fontWeight: 'bold'` on this header only.
 * With `layoutMode: 'grid'`, MRT already maps `align` to `justifyContent`.
 */
const centeredCellProps = {
  align: 'center' as const,
};

export type CreateStatusColumnOptions<T extends MRT_RowData> = {
  /** Renders the status indicator for each row. */
  Cell: NonNullable<MRT_ColumnDef<T>['Cell']>;
  /** Override the default `common.status` ("Estado") header. */
  header?: string | MessageDescriptor;
} & Omit<
  Partial<MRT_ColumnDef<T>>,
  'header' | 'Cell' | 'muiTableHeadCellProps' | 'muiTableBodyCellProps'
>;

/**
 * Builds a reusable MRT status column with centered header and cell content.
 * Header defaults to `common.status` ("Estado").
 */
export function createStatusColumn<T extends MRT_RowData>(
  $t: IntlFormatters['$t'],
  { Cell, header, ...columnDef }: CreateStatusColumnOptions<T>,
): MRT_ColumnDef<T> {
  const resolvedHeader =
    header == null
      ? $t(statusColumnHeaderMessage)
      : typeof header === 'string'
        ? header
        : $t(header);

  return {
    id: 'status',
    ...columnDef,
    header: resolvedHeader,
    Cell,
    muiTableHeadCellProps: centeredCellProps,
    muiTableBodyCellProps: centeredCellProps,
  };
}
